import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { competitionRoundsService } from '../../services/competition-rounds';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import { useGymAdmin } from '../context/gym-admin-context';
import type { CompetitionRound } from '../../types/competition-round';

/**
 * The rounds entry, which is not a page of its own any more.
 *
 * Every round hangs in the menu as a row of its own, and everything there is to
 * say about one is on the round's own two pages — its board and the setup behind
 * it. A list in the middle would be a third copy of the same names, so this
 * simply opens the first round: the menu entry stays clickable, and a link to
 * `/rounds` lands somewhere useful rather than nowhere.
 *
 * What is left for it to draw is the one thing the menu cannot say: a
 * competition with no rounds yet, and where the button that makes one is.
 */
export default function RoundsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { placePath } = useGymAdmin();
  const { location: competition, loading: competitionLoading, slug } = useCurrentLocation();

  const [rounds, setRounds] = useState<CompetitionRound[]>([]);
  const [listLoading, setListLoading] = useState(true);

  const load = useCallback(
    async (competitionSlug: string) => {
      setListLoading(true);
      try {
        setRounds(await competitionRoundsService.getRounds(competitionSlug));
      } catch (error) {
        toast({
          title: t('gymAdmin.rounds.loadError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  if (competitionLoading || listLoading) return <Skeleton className="h-48 w-full max-w-2xl" />;
  if (!competition || !slug) return null;

  // Replaced rather than pushed: this page has nothing to come back to.
  if (rounds.length > 0) {
    return <Navigate to={placePath(`/rounds/${rounds[0].id}`)} replace />;
  }

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>{t('gymAdmin.rounds.title')}</CardTitle>
        <CardDescription>{t('gymAdmin.rounds.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="no-rounds">
          {t('gymAdmin.rounds.empty')} {t('gymAdmin.rounds.createFromMenu')}
        </p>
      </CardContent>
    </Card>
  );
}
